import React, { useEffect, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { useSnackbar } from "notistack";
import { useTranslation } from "react-i18next";

import Sidebar from "../../components/Sidebar/Sidebar";
import useAuth from "../../hooks/useAuth";

import { listTenants } from "../../services/tenantSuperadminService";
import {
  issueGatewayRegistrationLink,
  listGatewayRegistrationLinks,
  invalidateGatewayRegistrationLink,
  sendGatewayRegistrationLinkEmail,
} from "../../services/gatewayService";

import TenantPickerCard from "./TenantPickerCard";
import IssueLinkCard from "./IssueLinkCard";
import RegLinksTable from "./RegLinksTable";

const computeDefaultSuffix = (tenant) => {
  if (!tenant) return "";
  const first = (tenant.contact_admin_first_name || "").trim();
  const last = (tenant.contact_admin_last_name || "").trim();
  const full = `${first} ${last}`.trim();
  return full || tenant.name || "";
};

export default function SuperAdminTenantsRegLinksPage() {
  const { isLoggedIn, user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const { t } = useTranslation();

  const [loadingTenants, setLoadingTenants] = useState(true);
  const [tenants, setTenants] = useState([]);

  const [selectedTenantId, setSelectedTenantId] = useState("");
  const selectedTenant = useMemo(() => {
    const idNum = Number(selectedTenantId);
    if (!idNum) return null;
    return tenants.find((x) => x.id === idNum) || null;
  }, [selectedTenantId, tenants]);

  const [issuing, setIssuing] = useState(false);
  const [ttlMinutes, setTtlMinutes] = useState(300);
  const [maxUses, setMaxUses] = useState(1);
  const [labelSuffixOverride, setLabelSuffixOverride] = useState("");
  const defaultSuffix = useMemo(() => computeDefaultSuffix(selectedTenant), [selectedTenant]);

  // ✅ email states
  const [emailTo, setEmailTo] = useState("");
  const [recipientName, setRecipientName] = useState("");
  const [sendingEmail, setSendingEmail] = useState(false);

  useEffect(() => {
    // auto-fill suffix whenever tenant changes
    setLabelSuffixOverride(defaultSuffix || "");
  }, [defaultSuffix]);

  // clear mail fields on tenant change
  useEffect(() => {
    setEmailTo("");
    setRecipientName("");
  }, [selectedTenantId]);

  const [issueResult, setIssueResult] = useState(null);

  const [loadingLinks, setLoadingLinks] = useState(false);
  const [links, setLinks] = useState([]);
  const [includeInactive, setIncludeInactive] = useState(false);

  const refreshTenants = async () => {
    setLoadingTenants(true);
    try {
      const data = await listTenants();
      const list = Array.isArray(data) ? data : data?.results || [];
      setTenants(list);
    } catch (e) {
      enqueueSnackbar(
        e?.message ||
          t("SuperAdminTenantsRegLinksPage.snackbar.failedToLoadTenants", "Nepodarilo sa načítať organizácie."),
        { variant: "error" }
      );
    } finally {
      setLoadingTenants(false);
    }
  };

  const refreshLinks = async (tenantId = selectedTenantId) => {
    const idNum = Number(tenantId);
    if (!idNum) return;

    setLoadingLinks(true);
    try {
      const res = await listGatewayRegistrationLinks({
        tenant_id: idNum,
        include_inactive: includeInactive,
      });

      if (!res.success) {
        enqueueSnackbar(
          res.error ||
            t("SuperAdminTenantsRegLinksPage.errors.listFailed", "Nepodarilo sa načítať odkazy."),
          { variant: "error" }
        );
        setLinks([]);
        return;
      }

      const list = Array.isArray(res.data) ? res.data : res.data?.results || [];
      setLinks(list);
    } catch {
      enqueueSnackbar(
        t("SuperAdminTenantsRegLinksPage.errors.listFailed", "Nepodarilo sa načítať odkazy."),
        { variant: "error" }
      );
    } finally {
      setLoadingLinks(false);
    }
  };

  useEffect(() => {
    if (isLoggedIn) refreshTenants();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoggedIn]);

  useEffect(() => {
    setIssueResult(null);
    setLinks([]);
    if (selectedTenantId) refreshLinks(selectedTenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTenantId]);

  useEffect(() => {
    if (selectedTenantId) refreshLinks(selectedTenantId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [includeInactive]);

  const copy = async (text, successKey) => {
    try {
      await navigator.clipboard.writeText(text);
      enqueueSnackbar(t(successKey, "Skopírované do schránky."), { variant: "success" });
    } catch {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.snackbar.copyFailed", "Kopírovanie zlyhalo."), {
        variant: "error",
      });
    }
  };

  const handleIssue = async () => {
    if (!selectedTenantId) {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.selectTenant", "Najprv vyberte organizáciu."), {
        variant: "error",
      });
      return;
    }

    const ttl = parseInt(ttlMinutes, 10);
    const max = maxUses === "" ? 1 : parseInt(maxUses, 10);

    if (!Number.isFinite(ttl) || ttl <= 0) {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.ttlPositive", "Platnosť musí byť kladné číslo."), {
        variant: "error",
      });
      return;
    }
    if (!Number.isFinite(max) || max <= 0) {
      enqueueSnackbar(
        t("SuperAdminTenantsRegLinksPage.errors.maxUsesPositive", "Maximálne použitia musí byť kladné číslo."),
        { variant: "error" }
      );
      return;
    }

    try {
      setIssuing(true);

      const payload = {
        tenant_id: Number(selectedTenantId),
        ttl_minutes: ttl,
        max_uses: max,
      };

      const trimmedOverride = (labelSuffixOverride || "").trim();
      if (trimmedOverride) payload.label_suffix = trimmedOverride;

      const res = await issueGatewayRegistrationLink(payload);

      if (!res.success) {
        enqueueSnackbar(
          t("SuperAdminTenantsRegLinksPage.errors.issueFailed", "Vydanie odkazu zlyhalo."),
          { variant: "error" }
        );
        return;
      }

      setIssueResult(res.data);
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.snackbar.issued", "Odkaz bol vydaný."), {
        variant: "success",
      });

      refreshLinks(selectedTenantId);
    } catch {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.issueFailed", "Vydanie odkazu zlyhalo."), {
        variant: "error",
      });
    } finally {
      setIssuing(false);
    }
  };

  const handleSendEmail = async () => {
    if (!selectedTenantId) {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.selectTenant", "Najprv vyberte organizáciu."), {
        variant: "error",
      });
      return;
    }

    const token = issueResult?.token;
    if (!token) {
      enqueueSnackbar(
        t("SuperAdminTenantsRegLinksPage.errors.noTokenToSend", "Najprv vygenerujte odkaz (token)."),
        { variant: "error" }
      );
      return;
    }

    const email = (emailTo || "").trim();
    if (!email) {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.emailRequired", "Zadajte e-mail adresu."), {
        variant: "error",
      });
      return;
    }

    try {
      setSendingEmail(true);

      const res = await sendGatewayRegistrationLinkEmail({
        tenant_id: Number(selectedTenantId),
        email,
        token,
        recipient_name: (recipientName || "").trim() || (labelSuffixOverride || "").trim(),
      });

      if (!res.success) {
        enqueueSnackbar(
          t("SuperAdminTenantsRegLinksPage.errors.sendEmailFailed", "Odoslanie e-mailu zlyhalo."),
          { variant: "error" }
        );
        return;
      }

      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.snackbar.emailSent", "E-mail bol odoslaný."), {
        variant: "success",
      });
    } catch {
      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.errors.sendEmailFailed", "Odoslanie e-mailu zlyhalo."), {
        variant: "error",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  const handleInvalidate = async (token) => {
    if (!selectedTenantId) return;

    try {
      const res = await invalidateGatewayRegistrationLink({
        tenant_id: Number(selectedTenantId),
        token,
      });

      if (!res.success) {
        enqueueSnackbar(
          t("SuperAdminTenantsRegLinksPage.errors.invalidateFailed", "Zneplatnenie odkazu zlyhalo."),
          { variant: "error" }
        );
        return;
      }

      enqueueSnackbar(t("SuperAdminTenantsRegLinksPage.snackbar.invalidated", "Odkaz bol zneplatnený."), {
        variant: "success",
      });

      refreshLinks(selectedTenantId);
    } catch {
      enqueueSnackbar(
        t("SuperAdminTenantsRegLinksPage.errors.invalidateFailed", "Zneplatnenie odkazu zlyhalo."),
        { variant: "error" }
      );
    }
  };

  if (!isLoggedIn) return null;

  return (
    <>
      <Sidebar user={user} />

      <Box sx={{ p: 2, display: "flex", justifyContent: "center" }}>
        <Box sx={{ width: "100%", maxWidth: 1100, display: "flex", flexDirection: "column", gap: 2 }}>
          <TenantPickerCard
            t={t}
            loadingTenants={loadingTenants}
            tenants={tenants}
            selectedTenant={selectedTenant}
            onSelectTenantId={setSelectedTenantId}
            refreshTenants={refreshTenants}
          />

          <IssueLinkCard
            t={t}
            selectedTenant={selectedTenant}
            ttlMinutes={ttlMinutes}
            setTtlMinutes={setTtlMinutes}
            maxUses={maxUses}
            setMaxUses={setMaxUses}
            issuing={issuing}
            onIssue={handleIssue}
            issueResult={issueResult}
            onCopy={copy}
            defaultSuffix={defaultSuffix}
            labelSuffixOverride={labelSuffixOverride}
            setLabelSuffixOverride={setLabelSuffixOverride}
            // ✅ email props
            emailTo={emailTo}
            setEmailTo={setEmailTo}
            recipientName={recipientName}
            setRecipientName={setRecipientName}
            sendingEmail={sendingEmail}
            onSendEmail={handleSendEmail}
          />

          <RegLinksTable
            t={t}
            selectedTenant={selectedTenant}
            links={links}
            loading={loadingLinks}
            includeInactive={includeInactive}
            setIncludeInactive={setIncludeInactive}
            onInvalidate={handleInvalidate}
            onRefresh={() => refreshLinks(selectedTenantId)}
            onCopy={copy}
          />
        </Box>
      </Box>
    </>
  );
}
